import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect, useState } from "react";

export default function MessageEditor({ value, onChange, onReady, editable = true }) {
  const [showPalette, setShowPalette] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#000000");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      FontFamily,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: value || "",
    editable: editable,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && onReady) onReady(editor);
  }, [editor]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  if (!editor) return null;

  const fontSizes = ["12px", "14px", "16px", "18px", "20px", "24px"];

  const colorSwatches = [
    "#000000", "#444444", "#666666", "#999999", "#CCCCCC", "#EEEEEE", "#FFFFFF", "#FF0000",
    "#FF9900", "#FFFF00", "#00FF00", "#00FFFF", "#0000FF", "#9900FF", "#FF00FF", "#800000",
    "#804000", "#808000", "#008000", "#008080", "#000080", "#400080", "#800080", "#FF8080",
    "#FFC080", "#FFFF80", "#80FF80", "#80FFFF", "#8080FF", "#C080FF", "#FF80FF", "#3F3F3F",
  ];

  const toolbarButton = (active) =>
    `px-2 py-1 text-xs border rounded-sm transition-colors focus:outline-none focus:ring-0 active:bg-gray-200 ${
      active
        ? "bg-white text-gray-900 border-gray-400"
        : "bg-gray-100 text-gray-700 border-gray-300"
    } hover:bg-white hover:text-gray-900`;

  return (
    <div className="inline-block rounded-md shadow-sm w-full">
      {editable && (
        <div className="flex flex-wrap items-center gap-3 bg-gray-100 border border-gray-300 rounded-t-md px-3 py-2">
        <select
          className="px-2 py-1 text-xs border border-gray-300 rounded-sm bg-white"
          onChange={(e) =>
            editor.chain().focus().setFontFamily(e.target.value || null).run()
          }
          defaultValue=""
        >
          <option value="">Font</option>
          <option value="Arial">Arial</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Verdana">Verdana</option>
        </select>

        <select
          className="px-2 py-1 text-xs border border-gray-300 rounded-sm bg-white"
          onChange={(e) =>
            editor
              .chain()
              .focus()
              .setMark("textStyle", {
                fontSize: e.target.value || null,
              })
              .run()
          }
          defaultValue=""
        >
          <option value="">Size</option>
          {fontSizes.map((size) => (
            <option key={size} value={size}>
              {parseInt(size, 10)}
            </option>
          ))}
        </select>

        <div className="w-px h-6 bg-gray-300" />

        <button
          type="button"
          title="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={toolbarButton(editor.isActive("bold"))}
        >
          <span className="font-bold ">B</span>
        </button>
        <button
          type="button"
          title="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={toolbarButton(editor.isActive("italic"))}
        >
          <span className="italic">I</span>
        </button>
        <button
          type="button"
          title="Underline"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={toolbarButton(editor.isActive("underline"))}
        >
          <span className="underline bg-transparent">U</span>
        </button>

        <div className="w-px h-6 bg-gray-300" />

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowPalette((v) => !v)}
            className={toolbarButton(false)}
          >
            <span className="flex items-center gap-2">
              <span
                className="inline-block w-3 h-3 border border-gray-300 rounded-sm"
                style={{ backgroundColor: selectedColor }}
              />
              <span>Color</span>
            </span>
          </button>
          {showPalette && (
            <div className="absolute right-0 mt-1 z-50 rounded-md border border-gray-300 bg-white shadow-lg p-2">
              <div className="grid grid-cols-8 gap-1 mb-2">
                {colorSwatches.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="w-4 h-4 border border-gray-200"
                    style={{ backgroundColor: c }}
                    onClick={() => {
                      editor.chain().focus().setColor(c).run();
                      setSelectedColor(c);
                      setShowPalette(false);
                    }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-6 h-6 border border-gray-300 rounded-sm"
                  style={{ backgroundColor: selectedColor }}
                />
                <input
                  type="text"
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded-sm w-24"
                />
                <button
                  type="button"
                  className="px-2 py-1 text-xs border border-gray-300 rounded-sm bg-gray-100 hover:bg-white"
                  onClick={() => {
                    editor.chain().focus().setColor(selectedColor).run();
                    setShowPalette(false);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-gray-300" />

        <select
          className="px-2 py-1 text-xs border border-gray-300 rounded-sm bg-white"
          onChange={(e) => {
            const value = e.target.value;
            if (!value) return;
            editor.chain().focus().setTextAlign(value).run();
          }}
          value={
            editor.isActive({ textAlign: "center" })
              ? "center"
              : editor.isActive({ textAlign: "right" })
              ? "right"
              : editor.isActive({ textAlign: "justify" })
              ? "justify"
              : "left"
          }
        >
          <option value="left">Align Left</option>
          <option value="center">Align Center</option>
          <option value="right">Align Right</option>
          <option value="justify">Justify</option>
        </select>
      </div>
      )}
      <EditorContent
        editor={editor}
        className={`bg-white min-h-[250px] p-3 border border-gray-300 rounded-b-md ${
          !editable ? "rounded-t-md bg-slate-50 opacity-90" : "border-t-0"
        }`}
      />
    </div>
  );
}
